<template>
  <Popover class="relative" v-has-enrollment>
    <PopoverButton
      v-has-enrollment
      ref="popOverButton"
      @click="handleClickVisibleFilter"
      @mouseover="hoverPopover"
      @mouseleave="leavePopover"
      class="inline-flex items-center gap-x-1 text-sm font-normal leading-6 bg-untitled-gray-100 hover:bg-untitled-gray-200 cursor-pointer focus:outline-none focus:border-untitled-gray-200 focus:shadow-outline-gray py-2 px-2 xl:px-3 border border-untitled-gray-100 rounded appearance-none text-untitled-gray-800 hover:text-untitled-gray-900"
    >
      <AcademicCapIcon class="w-4 text-untitled-gray-500" />
      <span>All Available Courses</span>
      <ChevronDownIcon class="h-5 w-5" aria-hidden="true" />
    </PopoverButton>

    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <PopoverPanel
        :class="[
          showCourses && !loadingCourses ? 'left-[24rem]' : 'left-48',
          'absolute z-10 mt-5 flex w-screen max-w-max -translate-x-1/2 px-1',
        ]"
      >
        <div
          :class="[showCourses && !loadingCourses ? 'max-w-3xl' : 'max-w-sm']"
          class="w-screen p-2 flex flex-row gap-x-2 overflow-hidden rounded-md bg-white text-sm leading-6 shadow-lg ring-1 ring-gray-900/5"
        >
          <div
            v-if="loadingCategories"
            class="flex flex-col gap-y-2 w-full p-1"
          >
            <el-skeleton :rows="7" animated />
          </div>
          <template v-else>
            <div
              :class="[
                'w-full',
                showCourses && !loadingCourses
                  ? 'border-r border-untitled-gray-200'
                  : '',
              ]"
            >
              <template v-if="categories.length">
                <div
                  v-for="item in categories"
                  @mouseover="handleClickCategory(item.id)"
                  :key="item.id"
                  @click="handleClickCategory(item.id)"
                  class="group flex flex-row relative cursor-pointer gap-x-6 py-1 rounded-lg px-1 hover:bg-gray-50"
                >
                  <div class="flex-1">
                    <span class="font-medium text-xs text-gray-900">
                      {{ item.name }}
                      <span class="absolute inset-0" />
                    </span>
                  </div>
                </div>
              </template>
              <div
                v-else
                class="w-full h-72 flex justify-center items-center font-semibold text-untitled-gray-500"
              >
                No Categories Available
              </div>
            </div>
            <div
              class="w-full flex flex-col justify-start items-start px-2"
              v-if="showCourses"
            >
              <template v-if="!loadingCourses">
                <template v-if="courses && courses.length">
                  <PopoverButton
                    class="w-full px-1 py-2 flex justify-start items-start flex-row hover:bg-gray-50 hover:rounded-md"
                    v-for="course in courses"
                    :key="course.id"
                    @click="handleClickCourse(course)"
                  >
                    <div class="flex-1 flex justify-start items-start text-xs text-left">
                      {{ course.name }}
                    </div>
                  </PopoverButton>
                </template>
                <template v-else>
                  <div
                    class="flex justify-center items-center w-full h-full font-bold"
                  >
                    No Courses
                  </div>
                </template>
              </template>
              <template v-else>
                <el-skeleton :rows="7" animated />
              </template>
            </div>
          </template>
        </div>
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<script setup>
import { useAllAvailableCourseStore } from "@/stores/useAllAvailableCourse.store";
import { AcademicCapIcon, ChevronDownIcon } from "@heroicons/vue/solid";
import { storeToRefs } from "pinia";
import { computed, inject, onMounted, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { useRouter } from "vue-router";
import { ElSkeleton } from "element-plus";
import { getRoles } from "@/composable/users/getRoles";

const roles = getRoles();

const courseSectionProvide = inject("fetchCourses");
const allAvailableCourseStore = useAllAvailableCourseStore();
const { loadingCategories, categories, courses, loadingCourses } = storeToRefs(
  allAvailableCourseStore
);
const allAvailableCoursesButton = ref(null);
const router = useRouter();
const visible = ref(false);
const showCourses = ref(false);
const selectedCategoryId = ref(null);
const popOverButton = ref(null);
const isHover = ref(false);
const isFetchHover = ref(false);

// close the mega menu
onClickOutside(allAvailableCoursesButton, () => (visible.value = false));

const handleClickCategory = async (categoryId) => {
  showCourses.value = true;
  selectedCategoryId.value = categoryId;

  const category = categories.value.find(
    (category) => category.id == categoryId
  );

  if (category && category.courses) {
    allAvailableCourseStore.handleFilterCourses(categoryId);
    isFetchHover.value = false;
    showCourses.value = true;
    return;
  }
};

const isManager = computed(() => {
  return (
    roles.isSuperAdmin.value ||
    roles.isAccountManager.value ||
    roles.isSpecificManager.value
  );
});

const handleClickCourse = (course) => {
  if (course.type === "scorm") {
    router.push({ name: "ScormCourseWatch", params: { id: course.scorm_provider_id } });
  }

  if (course.is_enrolled || isManager.value) {
    const id = course.unit_display
      ? course.unit_display.id
      : course.first_unit.id;

    router.push({ name: "units", params: { id: id } });
    return;
  }

  showCourses.value = false;
  courseSectionProvide.openViewCourse(course);
};

const handleClickVisibleFilter = async () => {
  visible.value = true;
};

const hoverPopover = async () => {
  if (popOverButton.value && !isHover.value) {
    popOverButton.value.el.click();
    isHover.value = true;
  }
};

onMounted(() => {
    allAvailableCourseStore.handleFetchCategories();
});
const leavePopover = () => {
  isHover.value = false;
};

</script>

<style lang="scss" scoped></style>
